import React, { useEffect, useState } from 'react';
import API from '../../api';

export default function GenerateSalarySlipModal({ show, onClose, formData, setFormData, onSubmit, employees }) {
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [showAttendanceStats, setShowAttendanceStats] = useState(null);
  const [error, setError] = useState(null);
  const [editableDaysWorked, setEditableDaysWorked] = useState('');

  // Set default month/year and defaults when modal opens
  useEffect(() => {
    if (show) {
      const currentDate = new Date();
      const defaultMonth = currentDate.getMonth() + 1;
      const defaultYear = currentDate.getFullYear();

      setFormData(prev => ({
        ...prev,
        month: prev.month || defaultMonth,
        year: prev.year || defaultYear,
        overtime_hours: prev.overtime_hours || 0,
        allowances: prev.allowances || 0,
        deductions: prev.deductions || 0
      }));
    }
  }, [show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === 'employee_id' ? parseInt(value) : value;

    setFormData(prev => ({ ...prev, [name]: newValue }));

    if (name === 'employee_id') {
      const emp = employees.find(e => e.id === parseInt(value));
      if (emp) {
        setFormData(prev => ({ ...prev, basic_salary: emp.salary.toString() }));
      }
    }
  };

  // Handle deductions separately and update formData
  const handleDeductionsChange = (e) => {
    const newValue = e.target.value;
    setAttendanceStats(prev => ({
      ...prev,
      monthlyAdvanceAmount: newValue
    }));
    setFormData(prev => ({
      ...prev,
      deductions: parseFloat(newValue) || 0
    }));
  };

  // Fetch attendance stats
  useEffect(() => {
    const fetchStats = async () => {
      const { employee_id, month, year } = formData;
      if (!employee_id || !month || !year) return;

      try {
        const { data } = await API.get(`/attendance/stats/${employee_id}?month=${month}&year=${year}`);
        setAttendanceStats(data);
        setShowAttendanceStats(data);
        setError(null);

        if (data?.totalWorkingDays && data?.totalDaysInMonth) {
          setFormData(prev => ({
            ...prev,
            days_worked: data.totalWorkingDays,
            total_days: data.totalDaysInMonth,
            deductions: data.monthlyAdvanceAmount || 0
          }));
          setEditableDaysWorked(data.totalWorkingDays);
        }
      } catch (err) {
        console.error('Failed to load attendance stats:', err);
        setAttendanceStats(null);
        setError('Failed to load attendance data');
      }
    };

    fetchStats();
  }, [formData.employee_id, formData.month, formData.year]);

  // Keep formData in sync with editableDaysWorked
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      days_worked: editableDaysWorked
    }));
  }, [editableDaysWorked]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Generate Salary Slip</h2>

        {showAttendanceStats && (
          <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mb-4 text-sm text-gray-700 space-y-1">
            <div><strong>Days Present:</strong> {showAttendanceStats.attendanceDays} Days</div>
            <div><strong>Leaves Taken:</strong> {showAttendanceStats.paidLeaveDays} Days</div>
            <div><strong>Holidays:</strong> {showAttendanceStats.holidayCount} Days</div>
            <div><strong>Total Working Days:</strong> {showAttendanceStats.totalWorkingDays} Days</div>
            <div><strong>Total Days in this month:</strong> {showAttendanceStats.totalDaysInMonth} Days</div>
            <div><strong>Advance Taken:</strong> ₹{showAttendanceStats.monthlyAdvanceAmount || 0}</div>
          </div>
        )}
        {error && <div className="text-red-500 text-sm mb-2">{error}</div>}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1">Employee</label>
              <select
                name="employee_id"
                value={formData.employee_id}
                onChange={handleInputChange}
                required
                className="w-full border px-3 py-2 rounded-lg"
              >
                <option value="">Select</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name} - {emp.employee_id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1">Month</label>
              <select
                name="month"
                value={formData.month}
                onChange={handleInputChange}
                required
                className="w-full border px-3 py-2 rounded-lg"
              >
                <option value="">Select</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1">Year</label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                required
                className="w-full border px-3 py-2 rounded-lg"
              />
            </div>
            <div>
              <label className="block mb-1">Basic Salary</label>
              <input
                type="number"
                name="basic_salary"
                value={formData.basic_salary}
                onChange={handleInputChange}
                required
                className="w-full border px-3 py-2 rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1">Allowances</label>
              <input
                type="number"
                name="allowances"
                value={formData.allowances}
                onChange={handleInputChange}
                className="w-full border px-3 py-2 rounded-lg"
              />
            </div>
            <div>
              <label className="block mb-1">Deductions</label>
              <input
                type="number"
                name="deductions"
                value={formData.deductions}
                onChange={handleDeductionsChange}
                className="w-full border px-3 py-2 rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block mb-1">Days Worked</label>
              <input
                type="number"
                name="days_worked"
                value={editableDaysWorked ?? ''}
                onChange={(e) => setEditableDaysWorked(e.target.value)}
                required
                className="w-full border px-3 py-2 rounded-lg"
              />
            </div>
            <div>
              <label className="block mb-1">Total Days</label>
              <input
                type="number"
                name="total_days"
                value={formData.total_days ?? ''}
                onChange={handleInputChange}
                required
                className="w-full border px-3 py-2 rounded-lg"
              />
            </div>
            <div>
              <label className="block mb-1">Overtime Hours</label>
              <input
                type="number"
                name="overtime_hours"
                value={formData.overtime_hours}
                onChange={handleInputChange}
                className="w-full border px-3 py-2 rounded-lg"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-900">
              Cancel
            </button>
            <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              Generate
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
